import { useCallback, useEffect, useState } from 'react';
import type { Editor as TinyMCEEditor } from 'tinymce';

export interface EditorSelectionInfo {
  text: string;
  rng: Range;
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

const TOOLBAR_CLEARANCE = 70;
const TOOLBAR_GAP = 10;
const EDITOR_BIND_POLL_MS = 150;

export default function useEditorSelection(
  editorRef: React.MutableRefObject<TinyMCEEditor | null>,
  enabled: boolean,
) {
  const [selection, setSelection] = useState<EditorSelectionInfo | null>(null);

  const clearSelection = useCallback(() => setSelection(null), []);

  useEffect(() => {
    if (!enabled) {
      setSelection(null);
      return;
    }

    let editor: TinyMCEEditor | null = null;

    function updateSelection() {
      if (!editor || editor.removed) return;

      const text = editor.selection.getContent({ format: 'text' });
      if (!text.trim()) {
        setSelection(null);
        return;
      }

      const probe = document.createElement('div');
      probe.innerHTML = editor.selection.getContent({ format: 'html' });
      if (probe.querySelector('a')) {
        setSelection(null);
        return;
      }

      const rng = editor.selection.getRng();
      const rangeRect = rng.getBoundingClientRect();
      const iframeRect = editor.iframeElement?.getBoundingClientRect();
      if (!iframeRect || (rangeRect.width === 0 && rangeRect.height === 0)) {
        setSelection(null);
        return;
      }

      const viewportTop = iframeRect.top + rangeRect.top;
      const placement = viewportTop > TOOLBAR_CLEARANCE ? 'top' : 'bottom';

      setSelection({
        text,
        rng: rng.cloneRange(),
        placement,
        top:
          placement === 'top'
            ? viewportTop + window.scrollY - TOOLBAR_GAP
            : iframeRect.top + rangeRect.bottom + window.scrollY + TOOLBAR_GAP,
        left:
          iframeRect.left +
          rangeRect.left +
          rangeRect.width / 2 +
          window.scrollX,
      });
    }

    const handleEditorEvent = () => updateSelection();

    const handleEditorKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelection(null);
    };

    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest?.('.ai-rephrase-toolbar')) {
        setSelection(null);
      }
    };

    const bindTimer = window.setInterval(() => {
      if (editorRef.current && !editorRef.current.removed) {
        window.clearInterval(bindTimer);
        editor = editorRef.current;
        editor.on('mouseup', handleEditorEvent);
        editor.on('keyup', handleEditorEvent);
        editor.on('keydown', handleEditorKeyDown);
      }
    }, EDITOR_BIND_POLL_MS);

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      window.clearInterval(bindTimer);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      if (editor && !editor.removed) {
        editor.off('mouseup', handleEditorEvent);
        editor.off('keyup', handleEditorEvent);
        editor.off('keydown', handleEditorKeyDown);
      }
    };
  }, [enabled, editorRef]);

  return { selection, clearSelection };
}
